import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { useTrailers } from './trailerData';
import DateTimePicker from './booking/DateTimePicker';
import useBookedDates from './booking/useBookedDates';
import { readDraftDates, writeDraftDates } from './booking/draftStorage';
import HomePage from './pages/HomePage';
import { BusinessSettingsProvider, useBusinessSettings } from './BusinessSettingsContext';
import { CustomerAuthProvider, useCustomerAuth } from './CustomerAuthContext';
import RequireCustomerAuth from './RequireCustomerAuth';
import { formatLocalTime, parseLocalDate } from './utils/dates';
import { resolveTrailerFee } from './utils/fees';
import { ProductSEO } from './components/SEO';
import { daysFromToday, getTodayYmd } from './booking/dateUtils';

// Lazy load non-critical routes for better initial load performance
const About = lazy(() => import('./About'));
const Contact = lazy(() => import('./Contact'));
const Admin = lazy(() => import('./admin/Admin'));
const TrailersPage = lazy(() => import('./pages/TrailersPage'));
const CustomerPaymentPage = lazy(() => import('./pages/CustomerPaymentPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const CancelBookingPage = lazy(() => import('./pages/CancelBookingPage'));
const CustomerBookingView = lazy(() => import('./pages/CustomerBookingView'));
const ManageAddonsPage = lazy(() => import('./pages/ManageAddonsPage'));
const SecurityDepositReauth = lazy(() => import('./pages/SecurityDepositReauth'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SampleAgreement = lazy(() => import('./pages/SampleAgreement'));

// Customer dashboard (Round 4)
const LoginPage = lazy(() => import('./pages/account/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/account/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/account/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/account/VerifyEmailPage'));
const AccountShell = lazy(() => import('./pages/account/AccountShell'));
const DashboardPage = lazy(() => import('./pages/account/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/account/ProfilePage'));
const MyBookingPage = lazy(() => import('./pages/account/MyBookingPage'));
const BookingTowVehiclePage = lazy(() => import('./pages/account/BookingTowVehiclePage'));
const SignedOutPage = lazy(() => import('./pages/account/SignedOutPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-spinner"></div>
    <p>Loading...</p>
  </div>
);

// Lazy wrapper for static pages
const LazyTermsPage = lazy(() => import('./pages/StaticPages').then(mod => ({ default: mod.TermsPage })));
const LazyPrivacyPage = lazy(() => import('./pages/StaticPages').then(mod => ({ default: mod.PrivacyPage })));
const LazyCancellationPage = lazy(() => import('./pages/StaticPages').then(mod => ({ default: mod.CancellationPage })));
const LazyFAQPage = lazy(() => import('./pages/StaticPages').then(mod => ({ default: mod.FAQPage })));

function App() {
  return (
    <HelmetProvider>
      <BusinessSettingsProvider>
        <CustomerAuthProvider>
          <Router>
            <div className="App">
              <Header />
              <main id="main-content">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/trailers" element={<TrailersPage />} />
                  <Route path="/trailer/:id" element={<TrailerDetailPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<LazyFAQPage />} />
                  <Route path="/rental-agreement" element={<SampleAgreement />} />
                  <Route path="/terms" element={<LazyTermsPage />} />
                  <Route path="/privacy" element={<LazyPrivacyPage />} />
                  <Route path="/cancellation-policy" element={<LazyCancellationPage />} />
                  <Route path="/book/:trailerId" element={<BookingPage />} />
                  <Route path="/pay/:bookingId/:token" element={<CustomerPaymentPage />} />
                  <Route path="/cancel/:bookingId/:token" element={<CancelBookingPage />} />
                  <Route path="/bookings/:bookingId/:token" element={<CustomerBookingView />} />
                  <Route path="/manage-addons/:bookingId/:token" element={<ManageAddonsPage />} />
                  <Route path="/reauth/:bookingId/:token" element={<SecurityDepositReauth />} />

                  {/* Customer auth (Round 4) — public routes */}
                  <Route path="/account/login" element={<LoginPage />} />
                  <Route path="/account/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/account/reset-password/:token" element={<ResetPasswordPage />} />
                  <Route path="/account/verify-email/:token" element={<VerifyEmailPage />} />
                  <Route path="/account/signed-out" element={<SignedOutPage />} />

                  {/* Customer dashboard — gated */}
                  <Route
                    path="/account"
                    element={
                      <RequireCustomerAuth>
                        <AccountShell />
                      </RequireCustomerAuth>
                    }
                  >
                    <Route index element={<DashboardPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="booking/:id" element={<MyBookingPage />} />
                    <Route path="booking/:id/tow-vehicle" element={<BookingTowVehiclePage />} />
                  </Route>

                  <Route path="/admin/*" element={<Admin />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
              </main>
              <Footer />
            </div>
          </Router>
        </CustomerAuthProvider>
      </BusinessSettingsProvider>
    </HelmetProvider>
  );
}

function Header() {
  const { settings } = useBusinessSettings();
  const { customer, loading: authLoading, logout } = useCustomerAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const handleSignOut = () => {
    logout();
    navigate('/account/signed-out', { replace: true });
  };

  // While CustomerAuthContext is doing its initial /me fetch, render
  // nothing on the auth side rather than flashing "Sign in" → "My account"
  // when the page first loads with a stored token.
  const renderAuthLinks = (extraClass = '') => {
    if (authLoading) return null;
    if (customer) {
      return (
        <>
          <Link to="/account" className={extraClass}>My account</Link>
          <button
            type="button"
            onClick={handleSignOut}
            className={`header-signout-btn${extraClass ? ` ${extraClass}` : ''}`}
          >
            Sign out
          </button>
        </>
      );
    }
    return <Link to="/account/login" className={extraClass}>Sign in</Link>;
  };

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <header className="header">
        <Link to="/" className="header-brand">
          {settings.logo_url && (
            <img
              src={settings.logo_url}
              alt={settings.business_name || ''}
              className="header-logo"
            />
          )}
          <h1 className="header-title">{settings.business_name}</h1>
        </Link>
        <nav aria-label="Main navigation">
          <Link to="/trailers">Browse Trailers</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Contact</Link>
          {renderAuthLinks()}
        </nav>
        <button
          type="button"
          className={`hamburger-btn${menuOpen ? ' open' : ''}`}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-drawer"
          onClick={() => setMenuOpen(o => !o)}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </header>
      <div
        className={`mobile-nav-overlay${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <nav
        id="mobile-nav-drawer"
        className={`mobile-nav-drawer${menuOpen ? ' open' : ''}`}
        aria-label="Mobile navigation"
      >
        <Link to="/trailers">Browse Trailers</Link>
        <Link to="/about">About</Link>
        <Link to="/contact">Contact</Link>
        {renderAuthLinks()}
      </nav>
    </>
  );
}

function TrailerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { trailers, loading: listLoading } = useTrailers();
  const { settings } = useBusinessSettings();
  const [currentImage, setCurrentImage] = useState(0);
  // Hydrate dates from the in-progress booking draft so a returning user
  // sees what they previously picked here. The booking form
  // (BookingForm.js) reads + writes the same storage key — see
  // ./booking/draftStorage.js. URL params still win downstream if the
  // user navigates via a bookmark with explicit dates.
  const initialDraft = readDraftDates(id);
  const [checkIn, setCheckIn] = useState(initialDraft?.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialDraft?.checkOut || '');
  // pickupTime / returnTime only matter for cargo (customer-pickable);
  // for RVs the time is locked to business_settings defaults and is
  // shown as a static caption under the date picker.
  const [pickupTime, setPickupTime] = useState(initialDraft?.pickupTime || '');
  const [returnTime, setReturnTime] = useState(initialDraft?.returnTime || '');
  const [dateError, setDateError] = useState('');
  const bookedDateStrings = useBookedDates(parseInt(id, 10) || null);
  // Direct-fetch fallback: the public trailer LIST endpoint still
  // filters to type='rv' (cargo discoverability is Phase 3 of the
  // cargo rollout), so a cargo trailer reached via direct link
  // wouldn't appear in the cached list. Fall back to GET /api/trailers/:id
  // which now serves both types.
  const [directTrailer, setDirectTrailer] = useState(null);
  const [directLoading, setDirectLoading] = useState(false);

  const trailerFromList = trailers.find(t => t.id === parseInt(id));
  const trailer = trailerFromList || directTrailer;

  useEffect(() => {
    if (listLoading) return;
    if (trailerFromList) return;
    let cancelled = false;
    setDirectLoading(true);
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/trailers/${id}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data?.success && data.trailer) {
          setDirectTrailer(data.trailer);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDirectLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, listLoading, trailerFromList]);

  if (listLoading || directLoading) {
    return <div className="detail-page"><h2>Loading...</h2></div>;
  }

  if (!trailer) {
    return <div className="detail-page"><h2>Trailer not found</h2></div>;
  }

  const isCargo = trailer.trailer_type === 'cargo';
  const halfDayMult = parseFloat(settings?.cargo_half_day_multiplier) || 1.25;
  const weekDayMult = parseFloat(settings?.cargo_week_day_multiplier) || 0.80;
  const dayRate = parseFloat(trailer.price_per_night) || 0;
  const cargoHalfDayPrice = dayRate * halfDayMult;
  const cargoWeekDayPrice = dayRate * weekDayMult;

  // "Today" resolved in the tenant timezone so the picker's minDate
  // matches the backend's advance-booking gate even when the browser
  // is in a different TZ. parseLocalDate yields a local-midnight Date
  // — react-datepicker compares calendar days, not instants.
  const todayDate = parseLocalDate(getTodayYmd(settings?.timezone));
  // Check-out must be on/after check-in (cargo same-day) or strictly
  // after (RV at least 1 night). Compute by walking the check-in Date
  // forward via setDate so DST boundaries don't drift.
  let checkOutMinDate = todayDate;
  if (checkIn) {
    const ci = parseLocalDate(checkIn);
    if (ci) {
      if (!isCargo) ci.setDate(ci.getDate() + 1);
      checkOutMinDate = ci;
    }
  }

  // Independent setters for each picker. The DateTimePicker emits YMD
  // strings directly (no TZ math here), and shared draft storage keeps
  // the trailer page + booking form in sync.
  const handleCheckInChange = (ymd) => {
    setDateError('');
    setCheckIn(ymd);
    // If a user moves check-in past the existing check-out, clear it
    // so they're forced to pick a new check-out (the picker's minDate
    // enforces validity going forward; this just resets the visible
    // pill).
    let nextCheckOut = checkOut;
    if (ymd && checkOut && ymd > checkOut) {
      nextCheckOut = '';
      setCheckOut('');
    }
    writeDraftDates(id, { checkIn: ymd, checkOut: nextCheckOut });
  };
  const handleCheckOutChange = (ymd) => {
    setDateError('');
    setCheckOut(ymd);
    writeDraftDates(id, { checkOut: ymd });
  };
  const handlePickupTimeChange = (hhmm) => {
    setPickupTime(hhmm);
    writeDraftDates(id, { pickupTime: hhmm });
  };
  const handleReturnTimeChange = (hhmm) => {
    setReturnTime(hhmm);
    writeDraftDates(id, { returnTime: hhmm });
  };

  // Calendar-day count. Cargo treats same-day (0 nights) as a valid
  // half-day booking; RV treats < 1 as no booking. The estimated-total
  // preview branches the same way.
  const nights = checkIn && checkOut
    ? (isCargo
        ? Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
        : Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)))
    : 0;
  // Trailer-level override wins over the business default — matches the
  // backend's COALESCE pattern in routes/bookings.js. Without this, the
  // customer was quoted the business default while the booking was
  // recorded with the override (B.6 smoke regression).
  const cleaningFee = resolveTrailerFee(trailer.cleaning_fee, settings.default_cleaning_fee, 150);
  let cargoTier = null;
  let cargoSubtotal = 0;
  if (isCargo && checkIn && checkOut && nights >= 0) {
    if (nights === 0) {
      cargoTier = 'half_day';
      cargoSubtotal = cargoHalfDayPrice;
    } else if (nights >= 7) {
      cargoTier = 'week';
      cargoSubtotal = cargoWeekDayPrice * nights;
    } else {
      cargoTier = 'day';
      cargoSubtotal = dayRate * nights;
    }
  }
  const estimatedTotal = isCargo
    ? (cargoTier ? cargoSubtotal + cleaningFee : 0)
    : (nights > 0 ? nights * trailer.price_per_night + cleaningFee : 0);

  const handleBookNow = () => {
    if (!checkIn || !checkOut) {
      setDateError('Please select your check-in and check-out dates');
      return;
    }
    // Mirror the backend gate (POST /api/bookings) and the booking-form
    // Zod schema. Reads from settings.min_advance_booking_days; defaults
    // to 2 (= the historical 48-hour minimum) if the setting hasn't
    // loaded yet or the field is missing on an older backend. "Today" is
    // resolved in the tenant's configured timezone so host TZ (UTC on
    // Railway) doesn't shift the cutoff.
    const minAdvanceDays =
      settings?.min_advance_booking_days != null
        ? Number(settings.min_advance_booking_days)
        : 2;
    const daysUntil = daysFromToday(checkIn, settings?.timezone);
    if (Number.isFinite(daysUntil) && daysUntil < minAdvanceDays) {
      setDateError(
        minAdvanceDays === 1
          ? 'Check-in must be at least 1 day from today'
          : `Check-in must be at least ${minAdvanceDays} days from today`
      );
      return;
    }
    navigate(`/book/${trailer.id}?checkIn=${checkIn}&checkOut=${checkOut}`);
  };

  return (
    <div className="detail-page">
      <ProductSEO trailer={trailer} url={`/trailer/${id}`} />

      <button className="back-btn" onClick={() => navigate('/')}>
        &larr; Back to All Trailers
      </button>

      <div className="detail-container">
        <div className="detail-left">
          <div className="detail-images">
            <img
              src={trailer.images[currentImage]}
              alt={trailer.name}
              className="detail-main-image"
              loading="eager"
            />
            <div className="image-thumbnails">
              {trailer.images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${trailer.name} thumbnail ${index + 1}`}
                  className={`thumbnail ${currentImage === index ? 'active' : ''}`}
                  loading="lazy"
                  onClick={() => setCurrentImage(index)}
                />
              ))}
            </div>
          </div>

          <div className="detail-info">
            <h1>{trailer.name}</h1>
            <p className="detail-brand">{trailer.brand}</p>
            <p className="detail-location">📍 {trailer.location}</p>

            <div className="detail-specs">
              {!isCargo && (
                <div className="spec-item">
                  <span className="spec-icon">👥</span>
                  <div>
                    <div className="spec-label">Sleeps</div>
                    <div className="spec-value">{trailer.sleeps} people</div>
                  </div>
                </div>
              )}
              {isCargo && (
                <div className="spec-item">
                  <span className="spec-icon">📦</span>
                  <div>
                    <div className="spec-label">Type</div>
                    <div className="spec-value">
                      {({ utility: 'Utility', enclosed: 'Enclosed', carhauler: 'Car Hauler', other: 'Cargo' }[trailer.cargo_subtype] || 'Cargo')} trailer
                    </div>
                  </div>
                </div>
              )}
              <div className="spec-item">
                <span className="spec-icon">📏</span>
                <div>
                  <div className="spec-label">Length</div>
                  <div className="spec-value">{trailer.length}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h2>Description</h2>
              <p>{trailer.description}</p>
            </div>

            <div className="detail-section">
              <h2>Features & Amenities</h2>
              <div className="features-grid">
                {trailer.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="feature-check">✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section">
              <h2>Rules & Requirements</h2>
              <ul className="rules-list">
                {trailer.rules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="detail-right">
          <div className="date-picker-card">
            <div className="date-picker-price">
              <span className="price-amount">${trailer.price_per_night}</span>
              <span className="price-label"> / {isCargo ? 'day' : 'night'}</span>
            </div>
            {isCargo && (
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.4 }}>
                Half-day: <strong>${cargoHalfDayPrice.toFixed(2)}</strong> &nbsp;·&nbsp;
                Week+: <strong>${cargoWeekDayPrice.toFixed(2)}/day</strong>
              </div>
            )}

            <div className="dtp-field-group">
              <label className="dtp-field-label">Check-in</label>
              <DateTimePicker
                value={checkIn}
                onChange={handleCheckInChange}
                mode={isCargo ? 'datetime' : 'date'}
                time={pickupTime}
                onTimeChange={handlePickupTimeChange}
                minDate={todayDate}
                bookedDateStrings={bookedDateStrings}
                placeholder="Select date"
                ariaLabel="Check-in date"
              />
              {!isCargo && settings?.default_pickup_time && (
                <small className="dtp-field-caption">
                  Pickup at {formatLocalTime(settings.default_pickup_time)}
                </small>
              )}
            </div>

            <div className="dtp-field-group">
              <label className="dtp-field-label">Check-out</label>
              <DateTimePicker
                value={checkOut}
                onChange={handleCheckOutChange}
                mode={isCargo ? 'datetime' : 'date'}
                time={returnTime}
                onTimeChange={handleReturnTimeChange}
                minDate={checkOutMinDate}
                bookedDateStrings={bookedDateStrings}
                placeholder="Select date"
                ariaLabel="Check-out date"
              />
              {!isCargo && settings?.default_return_time && (
                <small className="dtp-field-caption">
                  Return at {formatLocalTime(settings.default_return_time)}
                </small>
              )}
            </div>

            {dateError && <p className="date-picker-error">{dateError}</p>}

            {((isCargo && cargoTier) || (!isCargo && nights > 0)) && (
              <div className="date-picker-summary">
                <div className="summary-row">
                  <span>
                    {isCargo
                      ? cargoTier === 'half_day'
                        ? 'Half-day rental'
                        : cargoTier === 'week'
                          ? `Week+ rental (${nights} day${nights === 1 ? '' : 's'})`
                          : `Daily rental (${nights} day${nights === 1 ? '' : 's'})`
                      : `$${trailer.price_per_night} x ${nights} nights`}
                  </span>
                  <span>${(isCargo ? cargoSubtotal : nights * trailer.price_per_night).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Cleaning fee</span>
                  <span>${cleaningFee.toFixed(2)}</span>
                </div>
                <div className="summary-row summary-total">
                  <span>Estimated Total</span>
                  <span>${estimatedTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            <button className="book-now-btn" onClick={handleBookNow}>
              Book Now
            </button>

            <p className="date-picker-note">No payment required to submit your request</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Public-domain SimpleIcons paths for the platforms we support in
// business_settings. Adding a 7th platform is a one-line entry here +
// a Zod schema field + a DB column (migration). Order = render order
// in the footer.
const SOCIAL_ICONS = [
  {
    key: 'facebook_url',
    label: 'Facebook',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
  {
    key: 'instagram_url',
    label: 'Instagram',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  },
  {
    key: 'tiktok_url',
    label: 'TikTok',
    path: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z',
  },
  {
    key: 'youtube_url',
    label: 'YouTube',
    path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  },
  {
    key: 'twitter_url',
    label: 'Twitter / X',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  },
  {
    key: 'linkedin_url',
    label: 'LinkedIn',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
];

function SocialIcon({ href, label, path }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
        <path d={path} />
      </svg>
    </a>
  );
}

function Footer() {
  const { settings } = useBusinessSettings();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section">
          {settings.logo_url && (
            <img
              src={settings.logo_url}
              alt={settings.business_name || ''}
              className="footer-logo"
            />
          )}
          <h3>{settings.business_name}</h3>
          <p>{settings.tagline || 'Making outdoor adventures accessible'}</p>
          {SOCIAL_ICONS.some(({ key }) => settings[key]) && (
            <div className="social-links">
              {SOCIAL_ICONS.map(({ key, label, path }) => (
                <SocialIcon key={key} href={settings[key]} label={label} path={path} />
              ))}
            </div>
          )}
        </div>
        <div className="footer-section">
          <h4>Quick Links</h4>
          <Link to="/trailers">Browse Trailers</Link>
          <Link to="/about">About Us</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/faq">FAQ</Link>
        </div>
        <div className="footer-section">
          <h4>Legal</h4>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/cancellation-policy">Cancellation Policy</Link>
          <Link to="/rental-agreement">Sample Rental Agreement</Link>
        </div>
        <div className="footer-section">
          <h4>Contact</h4>
          {settings.contact_phone && <p>📞 {settings.contact_phone}</p>}
          {settings.contact_email && <p>📧 {settings.contact_email}</p>}
          {settings.contact_address && <p>📍 {settings.contact_address}</p>}
        </div>
      </div>
      <div className="footer-bottom">
        <p>© {currentYear} {settings.business_name} | All Rights Reserved</p>
        <a
          href="https://rentmehq.com"
          target="_blank"
          rel="noopener noreferrer"
          className="powered-by"
        >
          Powered by{' '}
          <img
            src="/rentmehq-logo-horizontal.svg"
            alt="RentMeHQ"
            className="powered-by-logo"
          />
        </a>
      </div>
    </footer>
  );
}

export default App;